import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Colors } from 'modules/theme';

const StepperContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  margin: 24px 0 32px 0;
  padding: 0 4px;
  @media (max-width: 767px) {
    margin: 20px 0 24px 0;
    padding: 0;
  }
`;

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  position: relative;
`;

const StepRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
`;

const Connector = styled.div`
  flex: 1;
  height: 2px;
  min-width: 12px;
  margin: 0 4px;
  align-self: center;
  background-color: ${props => (props.completed ? Colors.lightBlue : Colors.gray2)};
  transition: background-color 0.2s ease;
  @media (max-width: 767px) {
    min-width: 6px;
  }
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
  background-color: ${props => {
    if (props.completed) return Colors.lightBlue;
    if (props.active) return Colors.lightBlue;
    return Colors.white;
  }};
  color: ${props => {
    if (props.completed || props.active) return Colors.white;
    return Colors.gray;
  }};
  border: 2px solid
    ${props => {
      if (props.completed || props.active) return Colors.lightBlue;
      return Colors.gray2;
    }};
`;

const StepLabel = styled.span`
  margin-top: 10px;
  font-size: 14px;
  font-weight: ${props => (props.active ? 600 : 500)};
  color: ${props =>
    props.active ? Colors.lightBlue : props.completed ? Colors.gray8 : Colors.gray};
  text-align: center;
  line-height: 1.2;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 767px) {
    font-size: 11px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.15;
  }
`;

const StepButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
  background: none;
  border: none;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  padding: 0 4px 8px;
  &:focus {
    outline: none;
  }
  &:focus-visible ${StepCircle} {
    box-shadow: 0 0 0 2px ${Colors.lightBlue};
  }
`;

function Stepper({ steps, activeStep, onStepClick }) {
  const activeIndex =
    typeof activeStep === 'number'
      ? activeStep
      : steps.findIndex(s => (s.title || s.label || s) === activeStep);
  const currentIndex = activeIndex >= 0 ? activeIndex : 0;

  const content = [];
  steps.forEach((step, index) => {
    const title = typeof step === 'string' ? step : step.title || step.label || `Step ${index + 1}`;
    const completed = index < currentIndex;
    const active = index === currentIndex;
    const clickable = typeof onStepClick === 'function' && index < currentIndex;
    const isLast = index === steps.length - 1;

    content.push(
      <StepWrapper key={`step-${index}`}>
        <StepButton
          type="button"
          clickable={clickable}
          onClick={clickable ? () => onStepClick(index) : undefined}
          aria-current={active ? 'step' : undefined}
          aria-label={
            active
              ? `Current step: ${title}`
              : completed
              ? `Completed: ${title}`
              : `Step ${index + 1}: ${title}`
          }
        >
          <StepRow>
            <StepCircle completed={completed} active={active}>
              {completed ? (
                <span aria-hidden="true">✓</span>
              ) : (
                <span aria-hidden="true">{index + 1}</span>
              )}
            </StepCircle>
          </StepRow>
          <StepLabel active={active} completed={completed}>
            {title}
          </StepLabel>
        </StepButton>
      </StepWrapper>,
    );
    if (!isLast) {
      content.push(
        <Connector
          key={`connector-${index}`}
          completed={index < currentIndex}
          aria-hidden="true"
        />,
      );
    }
  });

  return (
    <StepperContainer
      role="progressbar"
      aria-valuenow={currentIndex + 1}
      aria-valuemin={1}
      aria-valuemax={steps.length}
    >
      {content}
    </StepperContainer>
  );
}

Stepper.propTypes = {
  activeStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onStepClick: PropTypes.func,
  steps: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        title: PropTypes.string,
      }),
    ]),
  ).isRequired,
};

export default Stepper;
